import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronDown, ShieldCheck, Home, Music, Target, Settings, LifeBuoy } from "lucide-react";
import SupportWidget from "@/components/SupportWidget";

const SECTIONS = [
  {
    id: "verification",
    icon: ShieldCheck,
    title: "Account Verification",
    body: [
      "When you join, we send a one-time code to your email (and phone). Entering it does one quiet, important thing: it seals your account into an encrypted, deeply private database envelope that only you can open.",
      "This is what keeps your safety net alive — if your writing ever signals a crisis, a verified route lets Aurea surface help and, if you've chosen to add one, gently reach your emergency contact.",
      "You're always in control: add or remove an emergency contact anytime in Settings, and erase everything with one tap whenever you wish.",
    ],
  },
  {
    id: "sanctuary",
    icon: Home,
    title: "Tab 1 · The Sanctuary",
    body: [
      "Your home — the choice-first Energy Gateway. Instead of a busy dashboard, Aurea simply asks how your heart feels, then meets you there. You're never asked to do more than you can.",
      "Emptiness Mode — for low, numb days. A calm heartbeat pulse and a single \"I'm Here\" anchor to hold onto. No tasks, just presence and a felt sense of being held.",
      "Chaos Mode — for racing, overloaded thoughts. Vent by voice or text onto a blank canvas; it's quietly saved and scanned for safety, so you can empty your mind without judgment.",
    ],
  },
  {
    id: "toolbox",
    icon: Music,
    title: "Tab 2 · The Toolbox",
    body: [
      "Your healing audio repository. Solfeggio tones (like 528 Hz for repair, 396 Hz for releasing fear) tune your nervous system with gentle, intentional frequencies.",
      "Binaural beats pair two tones to gently guide your brainwaves toward rest, meditation, calm focus or insight — headphones bring the effect to life.",
      "The Sound Sanctuary is a mood-correction crossfade engine: it reads the feeling in your words and blends soundscapes to gently move you from where you are toward steadier ground.",
    ],
  },
  {
    id: "momentum",
    icon: Target,
    title: "Tab 3 · The Momentum Engine",
    body: [
      "For higher-energy days when you're ready to build. Aurea reads your own recent reflections and gently shapes them into effortless structure — never a pressuring to-do list.",
      "A simple 2×2 priority grid surfaces your Hidden Core Desires (what you truly long for) against your Energy Drainers (what quietly depletes you), so you can see where your energy really wants to go.",
      "From there it maps low-pressure micro-goals across three horizons — Daily (3–5 minute steps), Weekly (gentle habits) and Yearly (a soft north star) — so momentum feels light, not heavy.",
    ],
  },
  {
    id: "settings",
    icon: Settings,
    title: "Tab 4 · My Settings",
    body: [
      "Your space, your control. Choose your Wisdom Lens to filter guidance between psychological (Jungian) and spiritual, nature-based, indigenous or scientific perspectives — Aurea speaks in the language that resonates with you.",
      "Manage your subscription tier here: stay on the always-useful free plan, or unlock Aurea Plus for unlimited reflections and the full tool library.",
      "This is also home to your privacy controls — including Wipe Canvas History and one-tap permanent deletion of everything.",
    ],
  },
];

const HelpGuide = ({ open, onClose }) => {
  const [openId, setOpenId] = useState("verification");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[75]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="help-guide-overlay"
        >
          <div className="absolute inset-0 bg-[#070d11]/80 backdrop-blur-md" onClick={onClose} />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0f1a20]/95 backdrop-blur-2xl border-l border-white/10 flex flex-col"
            data-testid="help-guide-panel"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 shrink-0">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-teal-200/50">Your guide</p>
                <h2 className="font-serif text-2xl text-white">Navigating Your Sanctuary</h2>
              </div>
              <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors" data-testid="help-guide-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
              <p className="text-white/55 text-[13px] leading-relaxed px-1">
                A gentle map of your whole sanctuary — what each part is, and the reason it was designed this way.
                Tap any section to open it.
              </p>

              {SECTIONS.map((s) => {
                const isOpen = openId === s.id;
                const Icon = s.icon;
                return (
                  <div key={s.id} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid={`help-section-${s.id}`}>
                    <button
                      onClick={() => setOpenId(isOpen ? null : s.id)}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                      data-testid={`help-accordion-${s.id}`}
                      aria-expanded={isOpen}
                    >
                      <span className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-teal-200/80" />
                      </span>
                      <span className="flex-1 text-white font-medium text-[15px]">{s.title}</span>
                      <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0 space-y-2.5">
                            {s.body.map((p, i) => (
                              <p key={i} className="text-white/55 text-[13px] leading-relaxed">{p}</p>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="help-support">
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                    <LifeBuoy className="w-4 h-4 text-teal-200/80" />
                  </span>
                  <h3 className="text-white font-medium text-[15px]">Report an Issue / Send Feedback</h3>
                </div>
                <p className="text-white/50 text-[12px] leading-relaxed mb-3">
                  Something not working, a login snag, or an idea to make Aurea gentler? Tell us — it goes
                  straight to the team.
                </p>
                <SupportWidget tone="dark" />
              </div>

              <p className="text-center text-white/30 text-xs px-4 pt-2 pb-4">
                Aurea supports self-awareness and is not a substitute for professional or emergency care.
                In crisis, call your local emergency number.
              </p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HelpGuide;
